import { useMemo } from "react";
import {
  Button,
  Form,
  Input,
  ModalFooter,
  errorToast,
} from "@telluscompany/design-system";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocalization, translateOperationalModule } from "@i18n";
import { updateLicenseSchema, type UpdateLicenseFormData } from "../../schemas";
import { useGetLicenseByIdQuery } from "../../api/licenses/licenses.queries";
import { LoadingBlurDiv, ReadOnlyField } from "@shared/components";
import { AxiosError } from "axios";

interface EditLicenseFormProps {
  tenantId: string;
  licenseId: string;
  onSubmit: (data: UpdateLicenseFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const EditLicenseForm = ({
  tenantId,
  licenseId,
  onSubmit,
  onCancel,
  isSubmitting,
}: EditLicenseFormProps) => {
  const { translate } = useLocalization();

  const { data: licenseData, isLoading, error, isError } = useGetLicenseByIdQuery(
    tenantId,
    licenseId,
  );

  if (isError) {
    const axiosError = error as AxiosError;
    errorToast({
      description: axiosError.parsedMessage ?? translate("common.errorEditing"),
    });
    onCancel();
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formValues = useMemo(
    () =>
      licenseData
        ? {
            managementEmail: licenseData.managementEmail,
            clientEmail: licenseData.clientEmail,
          }
        : undefined,
    [licenseData],
  );

  const form = useForm<UpdateLicenseFormData>({
    resolver: zodResolver(updateLicenseSchema(translate)),
    values: formValues,
  });

  return (
    <LoadingBlurDiv isLoading={isLoading}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="px-[32px] py-[16px] max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <ReadOnlyField
                label={translate("management.licenses.form.label.structure")}
                value={licenseData?.structure}
              />
              <ReadOnlyField
                label={translate("management.licenses.form.label.complex")}
                value={licenseData?.complex}
              />
              <ReadOnlyField
                label={translate("management.licenses.form.label.module")}
                value={translateOperationalModule(licenseData?.module, translate)}
              />
              <ReadOnlyField
                label={translate("management.licenses.form.label.licenses")}
                value={licenseData?.quantity?.toString()}
              />
              <ReadOnlyField
                label={translate("management.licenses.form.label.startDate")}
                value={licenseData ? formatDate(licenseData.startDate) : ""}
              />
              <ReadOnlyField
                label={translate("management.licenses.form.label.endDate")}
                value={licenseData ? formatDate(licenseData.endDate) : ""}
              />
              <Input
                control={form.control}
                label={translate("management.licenses.form.label.managerEmail")}
                name="managementEmail"
                type="email"
                placeholder={translate(
                  "management.licenses.form.placeholder.managerEmail",
                )}
              />
              <Input
                control={form.control}
                label={translate("management.licenses.form.label.clientEmail")}
                name="clientEmail"
                type="email"
                placeholder={translate(
                  "management.licenses.form.placeholder.clientEmail",
                )}
              />
              <ReadOnlyField
                label={translate(
                  "management.licenses.form.label.notificationPeriods",
                )}
                value={
                  licenseData?.notificationDays
                    ?.map(
                      (d) =>
                        `${d} ${translate("management.licenses.form.label.days")}`,
                    )
                    .join(", ")
                }
              />
              <ReadOnlyField
                label={translate("management.licenses.form.label.tenant")}
                value={licenseData?.tenant}
              />
            </div>
          </div>
          <ModalFooter
            footer={
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {translate("management.licenses.button.save")}
              </Button>
            }
            closeModal={
              <Button type="button" variant="secondary" onClick={onCancel}>
                {translate("management.licenses.button.cancel")}
              </Button>
            }
          />
        </form>
      </Form>
    </LoadingBlurDiv>
  );
};
